// ✅ Enhanced Admin Subscription Assignment Page with Delivery Generation UI
// Includes: Area column, Partner dropdown filtered by area, Bulk & Individual Assign, and Delivery Docs Creation

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, updateDoc, doc, query, where, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { Users, Truck, Eye, MapPin } from "lucide-react";

const AdminSubscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArea, setSelectedArea] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [showPaid, setShowPaid] = useState(true);
  const [showUnpaid, setShowUnpaid] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const subSnap = await getDocs(collection(db, "subscriptions"));
        const partnerSnap = await getDocs(query(collection(db, "users"), where("role", "==", "deliveryPartner")));

        const subscriptionsData = subSnap.docs.map((doc) => {
          const data = doc.data();
          // Ensure all required fields have fallback values
          return {
            id: doc.id,
            userName: data.userName || data.name || "Unknown Customer",
            userEmail: data.userEmail || data.email || "No Email",
            phone: data.phone || data.customerPhone || "No Phone",
            area: data.area || data.customerArea || "N/A",
            deliverySlot: data.deliverySlot || data.slot || "Morning",
            planName: data.planName || "Basic Plan",
            status: data.status || "active",
            paymentStatus: data.paymentStatus || "paid",
            address: data.address || data.addressDetails || data.customerAddress || "No Address",
            ...data // Keep all other fields
          };
        });
        
        // Debug logging for area field
        console.log("All subscriptions:", subscriptionsData);
        subscriptionsData.forEach(sub => {
          console.log(`Subscription ${sub.id}:`, {
            userName: sub.userName,
            area: sub.area,
            deliverySlot: sub.deliverySlot,
            paymentStatus: sub.paymentStatus,
            status: sub.status
          });
        });
        
        setSubscriptions(subscriptionsData);
        setPartners(partnerSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const generateDeliveries = async (sub, partner) => {
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const deliveryDate = new Date(today);
      deliveryDate.setDate(today.getDate() + i);
      const formattedDate = deliveryDate.toISOString().split("T")[0];
      const deliveryId = `${sub.id}_${formattedDate}`;

      await setDoc(doc(db, "deliveries", deliveryId), {
        subscriptionId: sub.id,
        userId: sub.userId,
        userName: sub.userName || sub.name || "",
        userEmail: sub.userEmail || sub.email || "",
        customerName: sub.userName || sub.name || "",
        customerPhone: sub.phone || "",
        customerEmail: sub.userEmail || sub.email || "",
        partnerId: partner.id,
        partnerName: partner.displayName || partner.name || "Unknown",
        area: sub.area || "",
        slot: sub.deliverySlot || "",
        address: sub.addressDetails || sub.address || "",
        addressDetails: sub.addressDetails || sub.address || "",
        // Add coordinates for maps
        lat: sub.lat || null,
        lng: sub.lng || null,
        coordinates: sub.coordinates || (sub.lat && sub.lng ? `${sub.lat},${sub.lng}` : null),
        addressForMaps: sub.addressForMaps || sub.addressDetails || sub.address || "",
        planName: sub.planName || "",
        date: formattedDate,
        status: "pending",
        createdAt: serverTimestamp()
      });
    }
  };

  const handleAssignPartner = async (subId, partnerId) => {
    if (!partnerId) return;
    try {
      setAssigning(true);
      const partner = partners.find(p => p.id === partnerId);
      const sub = subscriptions.find(s => s.id === subId);

      await updateDoc(doc(db, "subscriptions", subId), {
        deliveryPartnerId: partnerId,
        deliveryPartnerName: partner?.displayName || partner?.name || "Unknown",
        assignedAt: new Date().toISOString()
      });

      await generateDeliveries(sub, partner);

      setSubscriptions(prev => prev.map(sub =>
        sub.id === subId
          ? { ...sub, deliveryPartnerId: partnerId, deliveryPartnerName: partner?.displayName || partner?.name || "Unknown" }
          : sub
      ));

      alert("✅ Partner assigned and 30-day deliveries created for this order.");
    } catch (err) {
      console.error("Error assigning partner:", err);
      alert("❌ Failed to assign partner");
    } finally {
      setAssigning(false);
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedArea) return;
    try {
      setAssigning(true);
      const filteredSubs = subscriptions.filter((s) => s.area === selectedArea && !s.deliveryPartnerId);
      const filteredPartner = partners.find((p) => p.area === selectedArea);

      if (!filteredPartner) {
        alert("❌ No partner available for this area");
        return;
      }

      if (filteredSubs.length === 0) {
        alert("ℹ️ No unassigned subscriptions found for this area");
        return;
      }

      for (const sub of filteredSubs) {
        await updateDoc(doc(db, "subscriptions", sub.id), {
          deliveryPartnerId: filteredPartner.id,
          deliveryPartnerName: filteredPartner.displayName || filteredPartner.name || "Unknown",
          assignedAt: new Date().toISOString()
        });

        await generateDeliveries(sub, filteredPartner);
      }

      setSubscriptions(prev => prev.map(sub =>
        filteredSubs.some(fs => fs.id === sub.id)
          ? { ...sub, deliveryPartnerId: filteredPartner.id, deliveryPartnerName: filteredPartner.displayName || filteredPartner.name || "Unknown" }
          : sub
      ));

      alert(`✅ Bulk assignment completed for ${filteredSubs.length} subscriptions with 30-day deliveries.`);
    } catch (error) {
      console.error("Error in bulk assignment:", error);
      alert("❌ Failed to complete bulk assignment");
    } finally {
      setAssigning(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-600';
      case 'paused': return 'bg-yellow-100 text-yellow-600';
      case 'cancelled': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading subscriptions...</p>
        </div>
      </div>
    );
  }

  const uniqueAreas = [...new Set(subscriptions.map((s) => s.area).filter(Boolean))].sort();

  const filteredSubscriptions = subscriptions.filter(sub => {
    const paid = sub.paymentStatus === "paid";
    const shouldShow = (showPaid && paid) || (showUnpaid && !paid);
    
    // Debug logging for filtering
    console.log(`Filtering subscription ${sub.id}:`, {
      userName: sub.userName,
      area: sub.area,
      deliverySlot: sub.deliverySlot,
      paymentStatus: sub.paymentStatus,
      paid: paid,
      showPaid: showPaid,
      showUnpaid: showUnpaid,
      shouldShow: shouldShow
    });
    
    return shouldShow;
  });

  return (
    <div className="min-h-screen bg-[#f3fff6] p-6">
      <div className="text-sm text-green-600 mb-3">
        <button
          onClick={() => navigate("/admin/dashboard")}
          className="text-green-700 hover:underline font-medium hover:text-green-800"
        >
          ← Dashboard
        </button>{" "}
        / <b>Subscriptions</b>
      </div>
      <h1 className="text-2xl font-semibold text-green-800 mb-4">📦 Manage Subscriptions</h1>

      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-2">🚀 Bulk Assignment</h2>
        <div className="flex flex-col md:flex-row gap-3 items-center">
          <select
            className="border p-2 rounded w-full md:w-60"
            value={selectedArea}
            onChange={(e) => setSelectedArea(e.target.value)}
          >
            <option value="">Choose an area</option>
            {uniqueAreas.map(area => (
              <option key={area} value={area}>{area}</option>
            ))}
          </select>
          <button
            onClick={handleBulkAssign}
            disabled={assigning || !selectedArea}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            📋 Bulk Assign
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow">
        <div className="mb-4 flex gap-4 items-center">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showPaid}
              onChange={() => setShowPaid(v => !v)}
            />
            <span>Show Paid</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showUnpaid}
              onChange={() => setShowUnpaid(v => !v)}
            />
            <span>Show Unpaid</span>
          </label>
        </div>
        <h2 className="text-lg font-semibold mb-4">🧾 All Subscriptions ({filteredSubscriptions.length})</h2>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-green-100 text-green-800">
              <tr>
                <th className="text-left p-2">Customer</th>
                <th className="text-left p-2">Plan</th>
                <th className="text-left p-2">Area</th>
                <th className="text-left p-2">Slot</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Assigned Partner</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.map((sub) => (
                <tr key={sub.id} className="border-t">
                  <td className="p-2">
                    <div className="font-medium">{sub.userName || "Unknown Customer"}</div>
                    <div className="text-gray-500 text-xs">{sub.userEmail || "No Email"}</div>
                    {sub.phone && sub.phone !== "No Phone" && (
                      <div className="text-blue-700 text-xs">
                        <a href={`tel:${sub.phone}`}>{sub.phone}</a>
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    <span className="font-medium">{sub.planName || "Basic Plan"}</span><br />
                    <span className="text-xs text-gray-500">{sub.planDetails || `${sub.rotisPerDay || 2} rotis/day`}</span>
                  </td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      sub.area && sub.area !== "N/A" 
                        ? "bg-blue-100 text-blue-800" 
                        : "bg-gray-100 text-gray-600"
                    }`}>
                      {sub.area || "N/A"}
                    </span>
                  </td>
                  <td className="p-2">
                    <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                      {sub.deliverySlot || "Morning"}
                    </span>
                  </td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusColor(sub.status)}`}>
                      {sub.status || "active"}
                    </span>
                  </td>
                  <td className="p-2">
                    <select
                      value={sub.deliveryPartnerId || ""}
                      onChange={(e) => handleAssignPartner(sub.id, e.target.value)}
                      className="border p-1 rounded text-sm"
                    >
                      <option value="">-- Select Partner --</option>
                      {partners
                        .filter(p => p.area === sub.area)
                        .map(partner => (
                          <option key={partner.id} value={partner.id}>
                            {partner.displayName || partner.name} ({partner.area})
                          </option>
                        ))}
                    </select>
                  </td>
                  <td className="p-2 text-green-700">
                    <button 
                      onClick={() => navigate(`/admin/customers/${sub.userId}/calendar`)}
                      className="flex items-center text-sm gap-1 hover:text-green-800"
                    >
                      <Eye className="w-4 h-4" /> View Calendar
                    </button>
                    <button className="flex items-center text-sm gap-1 mt-1 hover:text-green-800">
                      <MapPin className="w-4 h-4" /> Track
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminSubscriptions;
